import { WeatherIcon } from "@Components/common/icon/WeatherIcon";
import { Bars } from "@Components/forecast/Bars";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import { DayForecastInterval } from "@MeteoApi/models/DayForecastInterval";
import { formatRainProbability } from "@Utils/format";
import classNames from "classnames";
import { RefObject, useState } from "react";

type DayProps = {
  label: string;
  day: DayForecastInterval;
  rainAmountInSegments: number[];
  isActive: boolean;
  symbolText: string;
  isIconAnimated: boolean;
  onSelect: () => void;
  keyboardNavigationRef: RefObject<boolean>;
};

export const Day = ({
  label,
  rainAmountInSegments = [],
  isActive,
  day,
  onSelect,
  symbolText,
  isIconAnimated,
  keyboardNavigationRef,
}: DayProps) => {
  const { t } = useTranslations();

  const {
    symbol24_code: symbolCode,
    TN_C: temperatureLow,
    TX_C: temperatureHigh,
    PROBPCP_PERCENT: rainProbability,
    RRR_MM: rainAmount,
  } = day;
  const isLessThanFivePercentProbability = rainProbability < 5;
  const [isHovered, setIsHovered] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    keyboardNavigationRef.current = e.key === "Enter";
  };

  return (
    <button
      type="button"
      className={classNames(
        "m:flex m:h-full m:w-full m:cursor-pointer m:flex-col m:items-stretch m:overflow-y-hidden m:border-0 m:p-0 m:pt-4 m:font-sans m:text-base m:leading-3 m:tracking-0.2 m:text-blue-950 m:transition-[background-color] m:delay-0 m:duration-[0.4s] m:ease-linear m:hover:font-bold m:md:pt-6 m:md:pb-4 m:md:text-base m:lg:pt-4 m:lg:pb-2 m:lg:text-2xl m:dark:text-neutral-white",
        {
          "m:bg-neutral-offwhite! m:font-bold! m:dark:bg-blue-950!":
            isActive || isHovered,
        },
        {
          "m:bg-warmgrey-25 m:dark:bg-blue-900": !isActive,
        },
        {
          "m:font-normal": !isActive && !isHovered,
        },
      )}
      data-testid="day-select"
      data-active={isActive}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      onKeyDown={(e) => handleKeyDown(e)}
      onClick={() => {
        onSelect();
        setIsHovered(false);
      }}>
      {isActive && (
        <span className="m:sr-only">{t("general.a11ySelected")}</span>
      )}
      <span
        className="m:block m:text-base m:lg:mt-0 m:lg:text-xl m:lg:leading-[14px] m:lg:tracking-0.4"
        data-testid="day-navigation-item-label">
        {label}
      </span>

      <WeatherIcon
        variant="forecast-day"
        symbolCode={symbolCode}
        altText={symbolText}
        animated={isIconAnimated}
        testId="day-navigation-item-symbol"
      />

      <span className="m:block m:gap-2 m:px-2 m:pt-1 m:pb-2 m:text-center m:text-base m:font-normal m:md:pt-2 m:md:text-base m:lg:text-xl m:lg:leading-[14px]">
        <span className="m:text-2xl m:leading-[16px] m:font-normal m:tracking-0.2 m:md:ml-auto m:lg:text-4xl m:lg:leading-5">
          <span
            role="presentation"
            aria-hidden="true"
            data-testid="day-navigation-item-temperature-high">{`${temperatureLow}° | `}</span>
          <span className="m:sr-only">
            {t("forecast.a11yTemperatureDuringNight", {
              temperature: temperatureLow,
            })}
          </span>
        </span>
        <span className="m:text-2xl m:leading-[16px] m:font-bold m:tracking-0.2 m:md:ml-auto m:lg:text-4xl m:lg:leading-5">
          <span
            role="presentation"
            aria-hidden="true"
            data-testid="day-navigation-item-temperature-low">{`${temperatureHigh}°`}</span>
          <span className="m:sr-only">
            {t("forecast.a11yTemperatureDuringDay", {
              temperature: temperatureHigh,
            })}
          </span>
        </span>
      </span>

      <div className="m:relative">
        <Bars values={rainAmountInSegments} unit="mm" scalingFactor={5} />
      </div>

      <div className="m:flex m:flex-col m:pt-2">
        <div
          className={classNames(
            "m:text-xl m:font-bold m:after:content-['_mm'] m:xs:text-base m:md:text-base m:lg:text-xl m:lg:leading-[14px]",
            {
              "m:text-warmgrey-700 m:dark:text-blue-400":
                isLessThanFivePercentProbability || rainAmount === 0,
            },
          )}>
          <span className="m:sr-only">{t("forecast.rainAmount")}</span>
          <span data-testid="day-navigation-item-rain-amount">{`${rainAmount}`}</span>
        </div>
        <div
          className={classNames(
            "m:text-xl m:after:content-['_%'] m:xs:text-base m:md:text-base m:lg:text-xl m:lg:leading-[14px]",
            {
              "m:text-warmgrey-700 m:after:content-['_%'] m:dark:text-blue-400":
                isLessThanFivePercentProbability || rainAmount === 0,
            },
          )}>
          <span className="m:sr-only">{t("forecast.rainProbability")}</span>
          <span data-testid="day-navigation-item-rain-probability">{`${formatRainProbability(rainProbability)}`}</span>
        </div>
      </div>
    </button>
  );
};
